import { Download, Loader2, X } from 'lucide-react';
import { useState } from 'react';
import Button from '../../../components/Button';
import InlineErrorDisplay from '../../../components/InlineErrorDisplay';
import type { ExportData, LearningLesson, LearningSection } from '../services/learningTypes';
import { exportImportService } from '../services/exportImportService';

interface ExportDialogProps {
    isOpen: boolean;
    onClose: () => void;
    section?: LearningSection;
    lessons?: LearningLesson[];
    mode: 'section' | 'lessons';
}

export default function ExportDialog({
    isOpen,
    onClose,
    section,
    lessons,
    mode,
}: ExportDialogProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedLessonIds, setSelectedLessonIds] = useState<string[]>([]);
    const [format, setFormat] = useState<'json' | 'text'>('json');

    if (!isOpen) return null;

    const handleExport = async () => {
        try {
            setLoading(true);
            setError(null);

            let exportData: ExportData;

            if (mode === 'section' && section) {
                exportData = await exportImportService.exportSection(section.uid);
            } else if (mode === 'lessons' && section && selectedLessonIds.length > 0) {
                exportData = await exportImportService.exportLessons(section.uid, selectedLessonIds);
            } else {
                throw new Error('Invalid export configuration');
            }

            if (format === 'text') {
                exportImportService.downloadAsTextFile(exportData);
            } else {
                exportImportService.downloadAsFile(exportData);
            }

            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to export data');
        } finally {
            setLoading(false);
        }
    };

    const handleLessonToggle = (lessonId: string) => {
        setSelectedLessonIds(prev =>
            prev.includes(lessonId)
                ? prev.filter(id => id !== lessonId)
                : [...prev, lessonId]
        );
    };

    const handleSelectAll = () => {
        if (lessons) {
            const allIds = lessons.map(lesson => lesson.uid);
            setSelectedLessonIds(selectedLessonIds.length === lessons.length ? [] : allIds);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-4 sm:p-6 border-b">
                    <div className="flex items-center gap-3">
                        <Download className="w-5 h-5 text-blue-600" />
                        <h2 className="text-xl font-semibold">
                            Export {mode === 'section' ? 'Section' : 'Lessons'}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
                    {mode === 'section' && section && (
                        <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h3 className="font-medium text-blue-900 mb-2">Section Details</h3>
                                <p className="text-blue-700">
                                    <strong>Title:</strong> {section.title}
                                </p>
                                <p className="text-blue-700">
                                    <strong>Lessons:</strong> {section.lessons.length}
                                </p>
                                <p className="text-blue-700 text-sm mt-2">
                                    This will export the entire section including all lessons and their pages.
                                </p>
                            </div>
                        </div>
                    )}

                    {mode === 'lessons' && lessons && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h3 className="font-medium">Select Lessons to Export</h3>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleSelectAll}
                                >
                                    {selectedLessonIds.length === lessons.length ? 'Deselect All' : 'Select All'}
                                </Button>
                            </div>

                            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                                {lessons.map((lesson) => (
                                    <label key={lesson.uid} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectedLessonIds.includes(lesson.uid)}
                                            onChange={() => handleLessonToggle(lesson.uid)}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate">{lesson.title}</p>
                                            <p className="text-sm text-gray-500 truncate">{lesson.description}</p>
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            {lesson.type} • {lesson.timeMinutes}min
                                        </div>
                                    </label>
                                ))}
                            </div>

                            {selectedLessonIds.length === 0 && (
                                <p className="text-amber-600 text-sm">
                                    Please select at least one lesson to export.
                                </p>
                            )}

                            {selectedLessonIds.length > 0 && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <p className="text-blue-700 text-sm">
                                        {selectedLessonIds.length} lesson{selectedLessonIds.length !== 1 ? 's' : ''} will be exported.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="mt-4">
                            <InlineErrorDisplay message={error} />
                        </div>
                    )}

                    <div className="mt-6">
                        <h4 className="font-medium text-gray-900 mb-3">Export Format</h4>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <label className="flex-1 flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors hover:bg-gray-50"
                                style={{ borderColor: format === 'json' ? '#2563eb' : undefined, backgroundColor: format === 'json' ? '#eff6ff' : undefined }}>
                                <input
                                    type="radio"
                                    name="exportFormat"
                                    value="json"
                                    checked={format === 'json'}
                                    onChange={() => setFormat('json')}
                                    className="w-4 h-4 text-blue-600"
                                />
                                <div>
                                    <p className="font-medium text-sm text-gray-900">JSON</p>
                                    <p className="text-xs text-gray-500">Full data with metadata, re-importable</p>
                                </div>
                            </label>
                            <label className="flex-1 flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors hover:bg-gray-50"
                                style={{ borderColor: format === 'text' ? '#2563eb' : undefined, backgroundColor: format === 'text' ? '#eff6ff' : undefined }}>
                                <input
                                    type="radio"
                                    name="exportFormat"
                                    value="text"
                                    checked={format === 'text'}
                                    onChange={() => setFormat('text')}
                                    className="w-4 h-4 text-blue-600"
                                />
                                <div>
                                    <p className="font-medium text-sm text-gray-900">Text</p>
                                    <p className="text-xs text-gray-500">Human-readable tagged format</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                        <h4 className="font-medium text-gray-900 mb-2">Export Information</h4>
                        {format === 'json' ? (
                            <ul className="text-sm text-gray-600 space-y-1">
                                <li>• Data will be exported as a JSON file</li>
                                <li>• The file includes all content, structure, and metadata</li>
                                <li>• You can import this file back into any db-admin instance</li>
                                <li>• The export is compatible with the current version only</li>
                            </ul>
                        ) : (
                            <ul className="text-sm text-gray-600 space-y-1">
                                <li>• Data will be exported as a plain text (.txt) file</li>
                                <li>• Pages use <code className="bg-gray-200 px-1 rounded text-xs">&lt;title&gt;</code>, <code className="bg-gray-200 px-1 rounded text-xs">&lt;page&gt;</code>, <code className="bg-gray-200 px-1 rounded text-xs">&lt;button&gt;</code>, and <code className="bg-gray-200 px-1 rounded text-xs">&lt;option&gt;</code> tags</li>
                                <li>• Multiple lessons are separated by <code className="bg-gray-200 px-1 rounded text-xs">---</code></li>
                                <li>• Not re-importable — use JSON for round-trip exports</li>
                            </ul>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t bg-gray-50">
                    <Button variant="outline" onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleExport}
                        disabled={loading || (mode === 'lessons' && selectedLessonIds.length === 0)}
                        className="min-w-[120px]"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                Exporting...
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4 mr-2" />
                                Export
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
